import { BundledItem, CartItem } from "@/networking/carts.api";
import { useTranslation } from "@/hooks/useTranslation";
import { FLUID_CDN_URL } from "@/lib/constants";

interface BundleGroupBlock {
  key: string;
  groupTitle: string | null;
  items: BundledItem[];
}

// Group bundled items by `product_bundle_group_id` so the admin's group
// title flows through to checkout as a header above each group's items.
// Untagged items (no group id) each land in their own block with a null
// title, which renders identically to the prior flat list — the legacy
// `ProductBundle` path always lands here.
function groupBundledItems(items: BundledItem[]): BundleGroupBlock[] {
  const blocks: BundleGroupBlock[] = [];
  const blocksByKey = new Map<string, BundleGroupBlock>();

  items.forEach((item, idx) => {
    const groupId = item.product_bundle_group_id;
    const key = groupId != null ? `g-${groupId}` : `flat-${idx}`;
    let block = blocksByKey.get(key);
    if (!block) {
      block = {
        key,
        groupTitle: item.group_title ?? null,
        items: [],
      };
      blocks.push(block);
      blocksByKey.set(key, block);
    }
    block.items.push(item);
  });

  return blocks;
}

export default function BundleItem({
  bundleItems,
  currencySymbol,
  totalSavings,
}: {
  bundleItems: CartItem["bundled_items"];
  currencySymbol: string;
  totalSavings?: number;
}) {
  const { t } = useTranslation();
  if (!bundleItems) return null;
  const blocks = groupBundledItems(bundleItems);
  return (
    <div className="my-1 pt-1 text-gray-700">
      <p className="mb-1 text-xs font-medium">{t("bundle_includes")}</p>
      <div className="space-y-0.5">
        {blocks.map((block) => {
          const hasHeader = !!block.groupTitle;
          return (
            <div key={block.key}>
              {hasHeader && (
                <div className="text-xs font-semibold text-gray-900">
                  {block.groupTitle}
                </div>
              )}
              <div className={hasHeader ? "space-y-0.5 pl-3" : "space-y-0.5"}>
                {block.items.map((bundleItem) => (
                  <div
                    key={bundleItem.variant_id}
                    className="flex items-center space-x-1"
                  >
                    <div className="h-1 w-1 shrink-0 rounded-full bg-gray-600"></div>
                    <span className="text-xs font-medium">
                      {bundleItem?.product_title || bundleItem?.variant_title}
                      {bundleItem.option_titles.length > 0 && (
                        <span className="ml-1 capitalize">
                          ({bundleItem.option_titles.join(", ")})
                        </span>
                      )}
                      <span className="ml-1">({bundleItem.quantity}x)</span>
                    </span>
                    {bundleItem.subscription && (
                      <span
                        role="img"
                        aria-label={t("subscribe_and_save")}
                        className="inline-block size-3 shrink-0 bg-blue-600"
                        style={(() => {
                          const maskUrl = `url(${FLUID_CDN_URL}/images/checkout/icons/arrows-repeat.svg)`;
                          return {
                            maskImage: maskUrl,
                            maskRepeat: "no-repeat",
                            maskSize: "contain",
                            maskPosition: "center",
                            WebkitMaskImage: maskUrl,
                            WebkitMaskRepeat: "no-repeat",
                            WebkitMaskSize: "contain",
                            WebkitMaskPosition: "center",
                          };
                        })()}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {totalSavings && totalSavings > 0 && (
          <div className="pt-0.5 leading-none">
            <span className="text-xs font-medium">
              {t("save")} {currencySymbol}
              {totalSavings.toFixed(2)} {t("compared_to_individual_purchase")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
